import React, { useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import ReactECharts from 'echarts-for-react';
import { differenceInMinutes } from 'date-fns';
import { pink } from '@mui/material/colors';
import useWindowDimensions from '../../hooks/useWindowDimensions';

// Be careful changing legend sort order, it must match data order

const ActionsChart = ({ actions, chartHeight, sendDateTime }) => {
    const { width } = useWindowDimensions();
    const ref = useRef(null);
    const theme = useTheme();
    const padding = theme.spacing(5);

    // eslint-disable-next-line no-undef
    const actionTypes = [...new Set(actions.map(e => e.type).sort((a, b) => a.localeCompare(b)))];

    const groupedByType = actions.reduce((acc, item) => {
        const { type, total, minutesAgo } = item;

        if (!acc[type]) {
            acc[type] = [];
        }

        acc[type].push([minutesAgo, total, type]);

        return acc;
    }, {});

    const data = Object.keys(groupedByType)
        .sort((a, b) => a.localeCompare(b))
        .map(type => groupedByType[type]);

    const sendDateTimeMinutesAgo = differenceInMinutes(
        new Date(),
        new Date(sendDateTime)
    );

    const maxSymbolSize = Math.floor(width / 4);

    const markLine = {
        silent: true,
        animation: false,
        label: {
            formatter: 'Sent',
            align: 'center'
        },
        lineStyle: {
            type: 'dotted',
            width: 2,
            color: pink[500]
        },
        tooltip: {
            formatter: 'Sent'
        },
        symbol: 'none',
        data: [
            {
                xAxis: sendDateTimeMinutesAgo
            }
        ]
    };

    const generateSeries = ({ name, index }) => {
        return {
            name,
            data: data[index],
            type: 'scatter',
            symbolSize: e => {
                const actionCount = e[1];

                return (actionCount < 10)
                    ? 10
                    : (actionCount > maxSymbolSize)
                        ? maxSymbolSize
                        : actionCount;
            },
            emphasis: {
                focus: 'series',
                label: {
                    show: true,
                    formatter: param => `${param.data[1]} ${param.seriesName}${param.data[1] !== 1 ? 's' : ''}`
                }
            },
            ...(index === 0 && (sendDateTimeMinutesAgo <= 60) && { markLine })
        };
    };

    const series = actionTypes.map((name, index) => generateSeries({ name, index }));

    const options = {
        title: {
            text: 'Actions are grouped into 5 minute intervals',
            textStyle: {
                fontWeight: 'normal',
                fontSize: '0.8rem'
            },
            top: theme.spacing(2),
            right: padding
        },
        grid: {
            containLabel: true,
            left: padding,
            top: theme.spacing(7),
            right: padding,
            bottom: padding
        },
        legend: {
            type: 'scroll',
            left: padding,
            top: theme.spacing(2),
            data: actionTypes,
            itemGap: 12
        },
        xAxis: {
            name: 'Minutes Ago',
            nameLocation: 'middle',
            nameGap: 30,
            min: 0,
            max: 60,
            interval: 5,
            minInterval: 5
            // todo highlight sentDateTime in this axis if it's on the scale?
        },
        yAxis: {
            name: 'Actions',
            nameLocation: 'middle',
            nameRotate: 90,
            nameGap: 30,
            scale: true,
            minInterval: 1
        },
        series
    };

    return (
        <ReactECharts
            option={options}
            style={{ height: chartHeight }}
            opts={{ renderer: 'svg' }}
            ref={ref}
        />
    );
};

export default ActionsChart;